import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  FunnelIcon,
  UserIcon,
  CpuChipIcon,
  ServerIcon,
  ShieldExclamationIcon,
  GlobeAltIcon,
  ClockIcon,
  EyeIcon,
  SignalIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';

// Category configuration
const categoryConfig = {
  ssh: { icon: ServerIcon, color: 'bg-purple-100 text-purple-700', label: 'SSH' },
  employee: { icon: UserIcon, color: 'bg-blue-100 text-blue-700', label: 'Employee' },
  user: { icon: UserIcon, color: 'bg-cyan-100 text-cyan-700', label: 'User' },
  device: { icon: CpuChipIcon, color: 'bg-green-100 text-green-700', label: 'Device' },
  api: { icon: GlobeAltIcon, color: 'bg-orange-100 text-orange-700', label: 'API' },
  ws: { icon: SignalIcon, color: 'bg-indigo-100 text-indigo-700', label: 'WebSocket' },
  security: { icon: ShieldExclamationIcon, color: 'bg-red-100 text-red-700', label: 'Security' },
  system: { icon: ServerIcon, color: 'bg-gray-100 text-gray-700', label: 'System' },
};

// WS endpoint icons
const wsEndpointIcons = {
  'device': CpuChipIcon,
  'subscriber': DevicePhoneMobileIcon,
  'admin-subscriber': ComputerDesktopIcon,
};

// WS event colors
const wsEventColors = {
  'connect': 'text-green-600 bg-green-50',
  'disconnect': 'text-gray-600 bg-gray-50',
  'auth_success': 'text-green-600 bg-green-50',
  'auth_failure': 'text-red-600 bg-red-50',
  'subscribe': 'text-blue-600 bg-blue-50',
  'unsubscribe': 'text-orange-600 bg-orange-50',
  'switch_topic': 'text-purple-600 bg-purple-50',
  'rate_limit': 'text-red-600 bg-red-50',
  'timeout': 'text-yellow-600 bg-yellow-50',
  'error': 'text-red-600 bg-red-50',
};

// Level colors
const levelConfig = {
  audit: { dot: 'bg-blue-500', text: 'text-blue-600', label: 'Audit' },
  info: { dot: 'bg-green-500', text: 'text-green-600', label: 'Info' },
  warn: { dot: 'bg-yellow-500', text: 'text-yellow-600', label: 'Warning' },
  error: { dot: 'bg-red-500', text: 'text-red-600', label: 'Error' },
  debug: { dot: 'bg-gray-400', text: 'text-gray-600', label: 'Debug' },
};

// Format relative time
function formatRelativeTime(timestamp) {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffSecs < 60) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function formatTimestamp(timestamp) {
  return new Date(timestamp).toLocaleString();
}

/**
 * EntityLink - Clickable entity that navigates to Activity Profile
 */
function EntityLink({ type, value, children, className = '' }) {
  const navigate = useNavigate();

  if (!value) {
    return <span className={`text-gray-400 ${className}`}>{children || '-'}</span>;
  }

  const handleClick = (e) => {
    e.stopPropagation();
    navigate(`/dashboard/logs/activity/${type}/${encodeURIComponent(value)}`);
  };

  return (
    <button
      onClick={handleClick}
      className={`group/entity inline-flex items-center gap-1 text-left hover:text-blue-600 transition-colors ${className}`}
      title={`View all activity for ${type}: ${value}`}
    >
      <span className="group-hover/entity:underline">{children || value}</span>
      <ArrowTopRightOnSquareIcon className="w-3 h-3 opacity-0 group-hover/entity:opacity-100 transition-opacity text-blue-500" />
    </button>
  );
}

// Get actor display for WebSocket logs
function getWsActorDisplay(log) {
  const actorType = log.actor?.type;
  const endpoint = log.context?.endpoint;

  if (actorType === 'device' || endpoint === 'device') {
    return {
      label: log.actor?.serial || log.actor?.name || '-',
      sublabel: log.actor?.firmware ? `v${log.actor.firmware}` : null,
      icon: CpuChipIcon,
      color: 'text-green-600',
    };
  }
  if (actorType === 'employee' || endpoint === 'admin-subscriber') {
    return {
      label: log.actor?.email || log.actor?.name || '-',
      sublabel: log.actor?.role || null,
      icon: ComputerDesktopIcon,
      color: 'text-blue-600',
    };
  }
  if (actorType === 'user' || endpoint === 'subscriber') {
    return {
      label: log.actor?.email || log.actor?.name || '-',
      sublabel: 'user',
      icon: DevicePhoneMobileIcon,
      color: 'text-cyan-600',
    };
  }
  return {
    label: log.actor?.email || log.actor?.name || log.actor?.ip || '-',
    sublabel: null,
    icon: UserIcon,
    color: 'text-gray-600',
  };
}

// Single log row component
function LogRow({ log, isSelected, onExpand }) {
  const category = categoryConfig[log.category] || categoryConfig.system;
  const level = levelConfig[log.level] || levelConfig.info;
  const CategoryIcon = category.icon;
  const isWsLog = log.category === 'ws';

  // Get WS-specific display info
  const wsActor = isWsLog ? getWsActorDisplay(log) : null;
  const WsActorIcon = wsActor?.icon || UserIcon;
  const wsEventColor = isWsLog ? (wsEventColors[log.action] || 'text-gray-600 bg-gray-50') : null;

  return (
    <tr
      onClick={() => onExpand(log)}
      className={`group cursor-pointer transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
    >
      {/* Time */}
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <ClockIcon className="w-4 h-4 text-gray-400" />
          <div>
            <p className="text-sm text-gray-900" title={formatTimestamp(log.timestamp)}>
              {formatRelativeTime(log.timestamp)}
            </p>
            <p className="text-xs text-gray-400">
              {new Date(log.timestamp).toLocaleTimeString()}
            </p>
          </div>
        </div>
      </td>

      {/* Category - Enhanced for WS logs */}
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${category.color}`}>
            <CategoryIcon className="w-3.5 h-3.5" />
            {category.label}
          </span>
          {isWsLog && (log.context?.endpoint || log.actor?.type) && (
            <span className="text-xs text-gray-400 pl-1">
              {log.context?.endpoint === 'device' ? 'Device' :
               log.actor?.type === 'employee' ? 'Employee' :
               log.actor?.type === 'user' ? 'User' :
               log.context?.endpoint === 'subscriber' ? 'Subscriber' :
               log.context?.endpoint === 'admin-subscriber' ? 'Admin' :
               log.context?.endpoint || '-'}
            </span>
          )}
        </div>
      </td>

      {/* Action - Enhanced for WS logs */}
      <td className="px-4 py-3">
        {isWsLog ? (
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${wsEventColor}`}>
            {log.action || '-'}
          </span>
        ) : (
          <span className="text-sm font-medium text-gray-900">{log.action || '-'}</span>
        )}
        {/* Show duration for disconnect events */}
        {isWsLog && log.action === 'disconnect' && log.context?.durationFormatted && (
          <span className="ml-2 text-xs text-gray-400">
            ({log.context.durationFormatted})
          </span>
        )}
      </td>

      {/* Actor - Enhanced for WS logs with clickable entities */}
      <td className="px-4 py-3">
        {isWsLog && wsActor ? (
          <div className="flex items-center gap-2">
            <div className={`w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center ${wsActor.color}`}>
              <WsActorIcon className="w-3.5 h-3.5" />
            </div>
            <div className="flex flex-col">
              {/* Clickable actor based on type */}
              {log.actor?.serial ? (
                <EntityLink type="device" value={log.actor.serial} className="text-sm text-gray-700 truncate max-w-[150px]">
                  {wsActor.label}
                </EntityLink>
              ) : log.actor?.email ? (
                <EntityLink type="email" value={log.actor.email} className="text-sm text-gray-700 truncate max-w-[150px]">
                  {wsActor.label}
                </EntityLink>
              ) : (
                <span className="text-sm text-gray-700 truncate max-w-[150px]">
                  {wsActor.label}
                </span>
              )}
              {wsActor.sublabel && (
                <span className="text-xs text-gray-400">{wsActor.sublabel}</span>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
              <UserIcon className="w-3.5 h-3.5 text-gray-500" />
            </div>
            {/* Clickable actor - prioritize email, then name/serial */}
            {log.actor?.email ? (
              <EntityLink type="email" value={log.actor.email} className="text-sm text-gray-700 truncate max-w-[150px]" />
            ) : log.actor?.serial ? (
              <EntityLink type="device" value={log.actor.serial} className="text-sm text-gray-700 truncate max-w-[150px]" />
            ) : (
              <span className="text-sm text-gray-700 truncate max-w-[150px]">
                {log.actor?.name || '-'}
              </span>
            )}
          </div>
        )}
      </td>

      {/* IP - Clickable */}
      <td className="px-4 py-3">
        {log.actor?.ip ? (
          <EntityLink type="ip" value={log.actor.ip} className="font-mono text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded hover:bg-blue-50" />
        ) : (
          <span className="font-mono text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">-</span>
        )}
      </td>

      {/* Message - Show topics for subscribe events */}
      <td className="px-4 py-3">
        <div className="flex flex-col">
          <p className="text-sm text-gray-600 truncate max-w-xs" title={log.message}>
            {log.message || '-'}
          </p>
          {isWsLog && log.context?.topics && (
            <span className="text-xs text-indigo-500">
              Topics: {Array.isArray(log.context.topics) ? log.context.topics.join(', ') : log.context.topics}
            </span>
          )}
        </div>
      </td>

      {/* Level */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${level.dot}`} />
          <span className={`text-xs font-medium ${level.text}`}>{level.label}</span>
        </div>
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <button
          onClick={(e) => { e.stopPropagation(); onExpand(log); }}
          className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-gray-200 rounded-lg transition-all"
        >
          <EyeIcon className="w-4 h-4 text-gray-500" />
        </button>
      </td>
    </tr>
  );
}

// Sortable column header
function SortableHeader({ label, sortKey, currentSort, onSort }) {
  const isActive = currentSort.key === sortKey;
  const direction = isActive ? currentSort.direction : null;

  return (
    <button
      onClick={() => onSort(sortKey)}
      className="flex items-center gap-1 text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 transition-colors"
    >
      {label}
      <div className="flex flex-col">
        <ChevronUpIcon
          className={`w-3 h-3 -mb-1 ${direction === 'asc' ? 'text-blue-600' : 'text-gray-300'}`}
        />
        <ChevronDownIcon
          className={`w-3 h-3 ${direction === 'desc' ? 'text-blue-600' : 'text-gray-300'}`}
        />
      </div>
    </button>
  );
}

export default function LogsTable({
  logs,
  loading,
  onLogSelect,
  selectedLog,
  pagination = null,
  onPageChange = null,
}) {
  const [sort, setSort] = useState({ key: 'timestamp', direction: 'desc' });
  const [localSearch, setLocalSearch] = useState('');

  // Handle sorting
  const handleSort = (key) => {
    setSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  // Sort and filter logs
  const processedLogs = useMemo(() => {
    let result = [...(logs || [])];

    // Local search filter
    if (localSearch) {
      const search = localSearch.toLowerCase();
      result = result.filter(log =>
        log.message?.toLowerCase().includes(search) ||
        log.action?.toLowerCase().includes(search) ||
        log.actor?.email?.toLowerCase().includes(search) ||
        log.actor?.ip?.includes(search)
      );
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[sort.key];
      let bVal = b[sort.key];

      if (sort.key === 'timestamp') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [logs, sort, localSearch]);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Table Header with Search */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <DocumentTextIcon className="w-5 h-5 text-gray-500" />
            <h3 className="font-medium text-gray-900">Log Entries</h3>
            <span className="text-sm text-gray-500">
              {processedLogs.length} {processedLogs.length === 1 ? 'entry' : 'entries'}
            </span>
          </div>

          {/* Local Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              placeholder="Search in results..."
              className="pl-9 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
            />
            {localSearch && (
              <button
                onClick={() => setLocalSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <SortableHeader
                  label="Time"
                  sortKey="timestamp"
                  currentSort={sort}
                  onSort={handleSort}
                />
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <SortableHeader
                  label="Action"
                  sortKey="action"
                  currentSort={sort}
                  onSort={handleSort}
                />
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actor
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Message
                </span>
              </th>
              <th className="px-4 py-3 text-left">
                <SortableHeader
                  label="Level"
                  sortKey="level"
                  currentSort={sort}
                  onSort={handleSort}
                />
              </th>
              <th className="px-4 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
              {loading ? (
                // Loading skeleton
                [...Array(10)].map((_, idx) => (
                  <tr key={`skeleton-${idx}`} className="animate-pulse">
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-20" /></td>
                    <td className="px-4 py-3"><div className="h-6 bg-gray-200 rounded w-24" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-16" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-32" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-24" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-48" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-12" /></td>
                    <td className="px-4 py-3"><div className="h-4 bg-gray-200 rounded w-8" /></td>
                  </tr>
                ))
              ) : processedLogs.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center">
                    <DocumentTextIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No logs found</p>
                    <p className="text-sm text-gray-400 mt-1">
                      {localSearch ? 'Try adjusting your search' : 'Adjust filters to see results'}
                    </p>
                  </td>
                </tr>
              ) : (
                processedLogs.map((log, idx) => (
                  <LogRow
                    key={log._id || idx}
                    log={log}
                    isSelected={selectedLog?._id === log._id}
                    onExpand={onLogSelect}
                  />
                ))
              )}
          </tbody>
        </table>
      </div>

      {/* Footer with Pagination */}
      {!loading && processedLogs.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            {/* Left: Info */}
            <div className="text-sm text-gray-500">
              {pagination && pagination.totalLogs > 0 ? (
                <span>
                  Showing {((pagination.page - 1) * (logs?.length || 0)) + 1}-{Math.min(pagination.page * (logs?.length || 0), pagination.totalLogs)} of {pagination.totalLogs.toLocaleString()} logs
                  {localSearch && ' (filtered locally)'}
                </span>
              ) : (
                <span>
                  Showing {processedLogs.length} of {logs?.length || 0} logs
                  {localSearch && ' (filtered)'}
                </span>
              )}
            </div>

            {/* Center: Pagination Controls */}
            {pagination && pagination.totalPages > 1 && onPageChange && (
              <div className="flex items-center gap-1">
                {/* First page */}
                <button
                  onClick={() => onPageChange(1)}
                  disabled={pagination.page === 1}
                  className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="First page"
                >
                  <ChevronDoubleLeftIcon className="w-4 h-4 text-gray-600" />
                </button>

                {/* Previous page */}
                <button
                  onClick={() => onPageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Previous page"
                >
                  <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
                </button>

                {/* Page numbers */}
                <div className="flex items-center gap-1 mx-2">
                  {/* Show page numbers around current page */}
                  {(() => {
                    const pages = [];
                    const { page, totalPages } = pagination;

                    // Calculate range of pages to show
                    let start = Math.max(1, page - 2);
                    let end = Math.min(totalPages, page + 2);

                    // Adjust if we're near the start or end
                    if (page <= 3) {
                      end = Math.min(5, totalPages);
                    }
                    if (page >= totalPages - 2) {
                      start = Math.max(1, totalPages - 4);
                    }

                    // First page if not in range
                    if (start > 1) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => onPageChange(1)}
                          className="px-2.5 py-1 text-sm rounded-lg hover:bg-gray-200 text-gray-600"
                        >
                          1
                        </button>
                      );
                      if (start > 2) {
                        pages.push(<span key="start-ellipsis" className="px-1 text-gray-400">...</span>);
                      }
                    }

                    // Page numbers
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => onPageChange(i)}
                          className={`px-2.5 py-1 text-sm rounded-lg transition-colors ${
                            i === page
                              ? 'bg-blue-600 text-white font-medium'
                              : 'hover:bg-gray-200 text-gray-600'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }

                    // Last page if not in range
                    if (end < totalPages) {
                      if (end < totalPages - 1) {
                        pages.push(<span key="end-ellipsis" className="px-1 text-gray-400">...</span>);
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => onPageChange(totalPages)}
                          className="px-2.5 py-1 text-sm rounded-lg hover:bg-gray-200 text-gray-600"
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}
                </div>

                {/* Next page */}
                <button
                  onClick={() => onPageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Next page"
                >
                  <ChevronRightIcon className="w-4 h-4 text-gray-600" />
                </button>

                {/* Last page */}
                <button
                  onClick={() => onPageChange(pagination.totalPages)}
                  disabled={pagination.page === pagination.totalPages}
                  className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  title="Last page"
                >
                  <ChevronDoubleRightIcon className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            )}

            {/* Right: Sort info */}
            <div className="text-sm text-gray-500">
              Sorted by {sort.key} ({sort.direction === 'asc' ? 'oldest' : 'newest'} first)
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
